import Axios from 'axios';
import { omit, pick, uniqBy } from 'lodash';
import {
  TCatalog,
  TPosition,
  catalogResponseSchema,
} from '../schema/catalog-schema';
import { buildImage, buildMeta, getAllCompaniesFromCatalog } from './helpers';
import { transformChain } from './transform-chain';
import { transformCity } from './transform-city';
import { transformCompany } from './transform-company';
import { transformCounty } from './transform-county';
import { Catalog } from './types';

const apiToken =
  'c68ff88cf3f7abb73cc748d0afc36be052637ee5e9b4ff2a8afe441d8c7f3d1160cf64d12a19286024252f54e2b65fc18c80e69836b685db99bbf97e66d6b17d4cf0c69521877aee9a1cfc566a6eded28958fd9f0e576ec3c6294e637cec66a517d71f4bf38fedf9b14039644f0db1d9c33d44f070090257998aa3f1b02dd400';

const axios = Axios.create({
  baseURL: 'http://catalog-strapi.nettbureau.dev:1337/api',
});

export async function getCatalogs() {
  let currentPage = 0;

  const response = await request(currentPage, 1);

  let catalogs = response.data;

  const totalPageCount = response.meta.pagination.pageCount;

  currentPage = 1;

  while (currentPage <= totalPageCount) {
    const response = await request(currentPage, 1);

    catalogs = [...catalogs, ...response.data];

    currentPage++;
  }

  return transform(catalogs);
}

async function request(page: number, size: number) {
  const response = await axios.get(
    `catalogs?pagination[page]=${page}&pagination[pageSize]=${size}&populate=deep,8`,
    {
      headers: {
        Authorization: `Bearer ${apiToken}`,
      },
    },
  );

  return catalogResponseSchema.parse(response.data);
}

export function transform(catalogs: TCatalog[]): Catalog[] {
  return catalogs.map((catalog) => {
    const baseUrl = [catalog.attributes.name];

    const companiesBaseUrl = [...baseUrl, catalog.attributes.companiesAlias];

    const transformedChains = catalog.attributes.chains.data.map((chain) =>
      transformChain(chain, { baseUrl: companiesBaseUrl, catalog }),
    );

    const transformedCompanies = getAllCompaniesFromCatalog(catalog).map(
      (company) =>
        transformCompany(company, { baseUrl: companiesBaseUrl, catalog }),
    );

    const offices = transformedCompanies.flatMap((company) => company.offices);

    const transformedCities = uniqBy(
      [
        ...catalog.attributes.cities.data,
        ...catalog.attributes.counties.data.flatMap(
          (county) => county.attributes.cities.data,
        ),
      ],
      'attributes.name',
    ).map((city) =>
      transformCity(city, {
        baseUrl,
        companiesBaseUrl,
        catalog,
        companies: transformedCompanies,
        offices,
      }),
    );

    const transformedCounties = catalog.attributes.counties.data.map((county) =>
      transformCounty(county, {
        baseUrl,
        companiesBaseUrl,
        catalog,
        offices,
        companies: transformedCompanies,
      }),
    );

    return {
      id: catalog.id,
      name: catalog.attributes.name,
      language: catalog.attributes.language.data?.attributes.locale ?? 'en',
      contactUrl: catalog.attributes.contactUrl ?? null,
      companyAlias: catalog.attributes.companiesAlias,
      defaultBackground: buildImage(catalog.attributes.defaultBackground),
      defaultTopImage: buildImage(catalog.attributes.defaultTopImage),
      configuration: catalog.attributes.configuration,
      chains: transformedChains,
      companies: transformedCompanies,
      cities: transformedCities,
      counties: transformedCounties,
      products: transformedCompanies.flatMap((company) => company.products),
      tableOfContents: catalog.attributes.tableOfContents.map((toc) => ({
        ...omit(toc, 'meta'),
        ...buildMeta({ attributes: pick(toc, 'meta') }),
        tags: toc.tags,
      })),
      positions:
        catalog.attributes.positions && catalog.attributes.positions.length > 0
          ? catalog.attributes.positions
          : ([
              { component: 'District', variant: 'districtPicker' },
              { component: 'City', variant: 'districtPicker' },
              { component: 'People', variant: 'default' },
              { component: 'Map', variant: 'secondary' },
              { component: 'Products', variant: 'default' },
              { component: 'Companies', variant: 'secondary' },
              { component: 'BasicCompanies', variant: 'default' },
            ] as TPosition[]),
    };
  });
}
